import { roundToPlaces } from "../../utils/roundToPlaces.js";
import { B2bFirstOrderDiscount } from "../B2bFirstOrderDiscount.js";
import type { CalculatedOrderDiscounts } from "../CalculatedOrderDiscounts.js";
import type { Country } from "../Country.js";
import type { Customer } from "../Customer.js";
import type { OrderInput } from "../Order.js";
import { OrderDiscountType } from "../OrderDiscountType.js";
import { b2bFirstOrderDiscountMinimumWeightGrams } from "../b2bFirstOrderDiscountMinimumWeightGrams.js";
import { b2bFirstOrderDiscountRate } from "../b2bFirstOrderDiscountRate.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";

export function calculateB2bFirstOrderDiscount(
	order: Pick<OrderInput, "products"> & { billingAddress?: { country?: Country } },
	weightInGrams: number,
	customer: Customer | null,
): CalculatedOrderDiscounts {
	const discounts: CalculatedOrderDiscounts = {
		discountsByTaxRate: [],
		freeDelivery: false,
	};

	if (customer === null) {
		return discounts;
	}

	if (customer.b2bFirstOrderDiscount !== B2bFirstOrderDiscount.On) {
		return discounts;
	}

	if (weightInGrams < b2bFirstOrderDiscountMinimumWeightGrams) {
		return discounts;
	}

	const byTaxRate = new Map<number, number>();
	for (const product of order.products) {
		const priceWithoutTax = byTaxRate.get(product.info.price.taxRate) ?? 0;
		byTaxRate.set(product.info.price.taxRate, priceWithoutTax + product.info.price.withoutTax * product.quantity);
	}

	for (const [taxRate, price] of byTaxRate) {
		const discountedPriceWithoutTax = roundToPlaces(price * b2bFirstOrderDiscountRate, 2);
		discounts.discountsByTaxRate.push({
			type: OrderDiscountType.B2bFirstOrder,
			taxRate,
			withoutTax: discountedPriceWithoutTax,
			withTax: calculatePriceWithTax(discountedPriceWithoutTax, taxRate),
		});
	}

	return discounts;
}
